import { useEffect, useRef, useState } from "react";
import { Bell, Check, X, Clock, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * ReminderBell — top-nav notification bell.
 * Polls /api/reminders/inbox every 30s, shows an unread badge,
 * opens a dropdown listing each reminder with Open / Complete / Snooze actions.
 */

const SNOOZE_PRESETS = [
    { label: "5 min",   minutes: 5 },
    { label: "10 min",  minutes: 10 },
    { label: "15 min",  minutes: 15 },
    { label: "30 min",  minutes: 30 },
    { label: "1 hour",  minutes: 60 },
    { label: "Tomorrow morning", key: "tomorrow_am" },
    { label: "Custom",  key: "custom" },
];

function tomorrowMorningISO() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(8, 0, 0, 0);
    return d.toISOString();
}

export default function ReminderBell() {
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const [snoozeFor, setSnoozeFor] = useState(null); // reminder id whose snooze menu is open
    const ref = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/reminders/inbox");
            setItems(data.items || []);
            setUnread(data.unread || 0);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 30_000);
        return () => clearInterval(id);
    }, []);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        window.addEventListener("mousedown", handler);
        return () => window.removeEventListener("mousedown", handler);
    }, [open]);

    const doAction = async (r, fn) => {
        try { await fn(); toast.success("Done"); await load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    };

    const openEvent = (r) => {
        setOpen(false);
        nav(`/calendar?event=${r.event_id}`);
    };

    const doSnooze = async (r, preset) => {
        setSnoozeFor(null);
        if (preset.key === "tomorrow_am") {
            await api.post(`/reminders/${r.id}/snooze`, { until_iso: tomorrowMorningISO() });
        } else if (preset.key === "custom") {
            const raw = window.prompt("Snooze for how many minutes?", "20");
            const mins = parseInt(raw, 10);
            if (!isFinite(mins) || mins <= 0) return;
            await api.post(`/reminders/${r.id}/snooze`, { minutes: mins });
        } else {
            await api.post(`/reminders/${r.id}/snooze`, { minutes: preset.minutes });
        }
        toast.success("Snoozed");
        await load();
    };

    return (
        <div ref={ref} className="relative" data-testid="reminder-bell">
            <button type="button" onClick={() => setOpen(o => !o)}
                    className="relative p-2 rounded-full hover:bg-black/5"
                    aria-label="Reminders" data-testid="reminder-bell-button">
                <Bell size={18} className="text-[var(--text-ink)]/75"/>
                {unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-[var(--brand-magenta)] text-white text-[10px] px-1 leading-4 text-center"
                          data-testid="reminder-bell-badge">
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>
            {open && (
                <div className="absolute right-0 mt-2 w-[360px] max-w-[92vw] card-warm p-2 z-[75]" data-testid="reminder-bell-dropdown">
                    <div className="flex items-center justify-between px-2 py-1">
                        <div className="text-xs uppercase tracking-wider text-[var(--text-ink)]/55">Reminders</div>
                        <div className="text-[10px] text-[var(--text-ink)]/45">{items.length}</div>
                    </div>
                    {items.length === 0 ? (
                        <div className="text-sm text-[var(--text-ink)]/55 px-2 py-6 text-center" data-testid="reminder-empty">
                            You&apos;re all caught up.
                        </div>
                    ) : (
                        <ul className="space-y-1 max-h-[420px] overflow-y-auto">
                            {items.map(r => (
                                <li key={r.id} className="p-2 rounded-lg hover:bg-black/[0.03] border border-black/5" data-testid={`reminder-item-${r.id}`}>
                                    <div className="flex items-start gap-2">
                                        <span className={`shrink-0 mt-1 w-1.5 h-1.5 rounded-full ${r.status === "delivered" ? "bg-[var(--brand-magenta)]" : "bg-[var(--text-ink)]/25"}`}/>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium truncate">{r.event_title}</div>
                                            <div className="text-[11px] text-[var(--text-ink)]/55 mt-0.5">
                                                <Clock size={10} className="inline mr-1"/>
                                                {new Date(r.event_start).toLocaleString()} · {r.offset_minutes === 0 ? "now" : `${r.offset_minutes}m before`}
                                            </div>
                                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                                                <button onClick={() => openEvent(r)}
                                                        className="text-[10px] px-2 py-1 rounded btn-ghost inline-flex items-center gap-1"
                                                        data-testid={`reminder-open-${r.id}`}>
                                                    <ExternalLink size={10}/> Open
                                                </button>
                                                <button onClick={() => doAction(r, () => api.post(`/reminders/${r.id}/complete`, { mark_event_complete: false }))}
                                                        className="text-[10px] px-2 py-1 rounded btn-ghost inline-flex items-center gap-1"
                                                        data-testid={`reminder-complete-${r.id}`}>
                                                    <Check size={10}/> Complete
                                                </button>
                                                <div className="relative">
                                                    <button onClick={() => setSnoozeFor(snoozeFor === r.id ? null : r.id)}
                                                            className="text-[10px] px-2 py-1 rounded btn-ghost inline-flex items-center gap-1"
                                                            data-testid={`reminder-snooze-${r.id}`}>
                                                        <Clock size={10}/> Snooze
                                                    </button>
                                                    {snoozeFor === r.id && (
                                                        <div className="absolute left-0 mt-1 card-warm p-1 z-[65] w-40" data-testid={`reminder-snooze-menu-${r.id}`}>
                                                            {SNOOZE_PRESETS.map(p => (
                                                                <button key={p.label} onClick={() => doSnooze(r, p)}
                                                                        className="w-full text-left text-[11px] px-2 py-1 rounded hover:bg-black/5"
                                                                        data-testid={`reminder-snooze-opt-${p.label.replace(/\s+/g,'-')}-${r.id}`}>
                                                                    {p.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <button onClick={() => doAction(r, () => api.post(`/reminders/${r.id}/dismiss`))}
                                                        className="text-[10px] px-2 py-1 rounded btn-ghost inline-flex items-center gap-1 ml-auto text-[var(--text-ink)]/55"
                                                        data-testid={`reminder-dismiss-${r.id}`}>
                                                    <X size={10}/>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
